/**
 * Real amber pharmaceutical vial photo for the browse-medications catalog and
 * the logged-in `/medications` picker.  Source image (`/vials/amber-vial.jpg`)
 * is a tightly-cropped photo of a generic amber injectable vial with a gold
 * crimp band and translucent plastic flip-off cap — fully unbranded, so it
 * complies with our legal posture of "Not affiliated with Novo Nordisk or Eli
 * Lilly". Real dispensed vials will be labelled by the licensed compounding
 * pharmacy, which is why we keep the "Illustration" disclaimer pill + the
 * pharmacy-branding disclaimer beneath the card grid.
 *
 * `variant` controls a subtle CSS hue-shift so Semaglutide and Tirzepatide
 * cards stay visually distinguishable without needing two different photos:
 *   - "semaglutide"  → no hue shift (natural amber)
 *   - "tirzepatide" → small +25° hue rotate to lean slightly more red-amber
 */
import { motion } from "framer-motion";

const VARIANT_FILTERS = {
  semaglutide: "none",
  tirzepatide: "hue-rotate(-15deg) saturate(1.15)",
};

export default function VialIllustration({ variant = "semaglutide", className = "" }) {
  const filter = VARIANT_FILTERS[variant] || VARIANT_FILTERS.semaglutide;

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center ${className}`}
      data-testid={`vial-illustration-${variant}`}
      aria-label={`Amber medication vial illustration for ${variant}`}
      role="img"
    >
      {/* Soft purple aurora glow behind the vial */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            "radial-gradient(ellipse at 50% 60%, rgba(167,139,250,0.28), rgba(255,255,255,0) 65%)",
        }}
      />
      <motion.img
        src="/vials/amber-vial.jpg"
        alt=""
        aria-hidden
        draggable={false}
        className="relative max-h-full w-auto object-contain select-none transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-1"
        style={{
          filter,
          mixBlendMode: "multiply", // lets the purple gradient backdrop show through the white margins
        }}
        initial={{ y: 6, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
    </div>
  );
}
